import { useEffect, useRef } from 'react';

/**
 * A hivas a TELEFON reszekent viselkedik (versenyaudit K2).
 *
 * Harom dolog, ami egy natív hivo appban magatol ertetodo, a weben viszont
 * kerni kell:
 *
 *  - **Ebren marad a kepernyo** (Wake Lock). Enelkul fel perc csend utan a
 *    telefon lezar - iOS-en a telepitett PWA ilyenkor a mikrofont is elengedi,
 *    es a hivas megszakad.
 *  - **Hivaskent szol a hang** (iOS `audioSession`). Alapbol a "lejatszas"
 *    modban a mikrofon megnyitasa utan a hangszoro halk vagy a fulhallgato
 *    kimarad; a `play-and-record` a telefonhivasok modja.
 *  - **A rendszer tudja, hogy hivas megy** (Media Session). A zarolt kepernyon
 *    es a fulhallgato gombjan a nemitas es a letetel mukodik.
 *
 * Mindharom csak ott lep eletbe, ahol a bongeszo ismeri - mashol csendben
 * kimarad, a hivas ettol meg megy.
 */
export function useCallDevice(options: {
  active: boolean;
  title: string;
  muted: boolean;
  onToggleMute: () => void;
  onHangup: () => void;
}): void {
  const { active, title, muted } = options;
  const handlers = useRef(options);
  handlers.current = options;

  // ─── Ebren tartas ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let disposed = false;

    const acquire = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const next = await navigator.wakeLock.request('screen');
        if (disposed) void next.release();
        else lock = next;
      } catch {
        // Akkumulator-kimelo mod, vagy a bongeszo nem engedi - nem baj.
      }
    };

    // A zar a lap elrejtesekor magatol elenged; visszaterve ujra kerjuk.
    const onVisible = () => void acquire();
    void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);

  // ─── iOS hangmod ────────────────────────────────────────────────────────────
  useEffect(() => {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (!active || !session) return;
    const previous = session.type;
    try {
      session.type = 'play-and-record';
    } catch {
      return;
    }
    return () => {
      try {
        session.type = previous;
      } catch {
        // nincs mit tenni
      }
    };
  }, [active]);

  // ─── Media Session ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !('mediaSession' in navigator)) return;
    const media = navigator.mediaSession;
    media.metadata = new MediaMetadata({ title, artist: 'AVSlack hívás' });

    const set = (action: string, handler: (() => void) | null) => {
      try {
        media.setActionHandler(action as MediaSessionAction, handler);
      } catch {
        // A bongeszo nem ismeri ezt a muveletet.
      }
    };
    set('togglemicrophone', () => handlers.current.onToggleMute());
    set('hangup', () => handlers.current.onHangup());

    return () => {
      set('togglemicrophone', null);
      set('hangup', null);
      media.metadata = null;
    };
  }, [active, title]);

  useEffect(() => {
    if (!active || !('mediaSession' in navigator)) return;
    const media = navigator.mediaSession as MediaSession & {
      setMicrophoneActive?: (active: boolean) => unknown;
    };
    /*
      NEM ad vissza igeretet: a Chrome-ban `void`. Egy `.catch` ra az egesz
      hivas-ablakot elvitte ("Cannot read properties of undefined") - ezert
      csak hivjuk, es ha a bongeszo dob, elnyeljuk.
    */
    try {
      void Promise.resolve(media.setMicrophoneActive?.(!muted)).catch(() => undefined);
    } catch {
      // A bongeszo nem ismeri vagy elutasitja - a hivast ez nem erintheti.
    }
  }, [active, muted]);
}
